import { useCallback, useEffect, useRef, useState } from "react";
import * as ScreenOrientation from "expo-screen-orientation";
import * as SplashScreen from "expo-splash-screen";
import { initializeLegacyKit } from "@src/config/legacyKitConfig";
import { legacyKitLifecycle } from "modules/legacy-kit";
import { reinitializeAuthAfterLegacyMigration, useAuthStore } from "@src/features/auth";
import { migrateLegacyAuthData, handleAppModeTransition } from "@src/features/auth/utils/legacy-storage-migration";
import {
  initFeatureFlags,
  getBooleanFlag,
  FEATURE_FLAGS,
  hydrateFeatureFlagsFromCache,
} from "@src/services/feature-flags/feature-flags";
import { initAmplitude } from "@src/services/analytics/amplitude";
import { genericStorage } from "@src/services/app-storage";
import { networkReachability } from "@src/utils/network";
import { isE2ETest } from "@src/modules/LaunchArguments";
import type { UseLegacyAppModeReturn } from "./useLegacyAppMode";
import { useIsMountedRef } from "./useIsMountedRef";

/** Maximum time to wait for boot to complete before defaulting to RN mode. */
const BOOT_TIMEOUT_MS = 10_000;

type UseAppBootOptions = Pick<
  UseLegacyAppModeReturn,
  "setShouldShowReactNativeScreens" | "setFeatureFlagsFailedToLoad" | "handlePresentLegacyApp"
> & {
  isTenantResolved: boolean;
  checkForceUpgrade: () => void;
};

/** Boot sequence — runs once on mount. Resolves orientation, auth, flags, then sets app mode. */
export function useAppBoot({
  setShouldShowReactNativeScreens,
  setFeatureFlagsFailedToLoad,
  handlePresentLegacyApp,
  isTenantResolved,
  checkForceUpgrade,
}: UseAppBootOptions): void {
  const [pendingShowLegacy, setPendingShowLegacy] = useState<boolean | null>(null);
  const isMountedRef = useIsMountedRef();
  const finalisedRef = useRef(false);

  const finaliseBoot = () => {
    if (finalisedRef.current) return;
    finalisedRef.current = true;
    SplashScreen.setOptions({ fade: true, duration: 600 });
    void SplashScreen.hideAsync();
  };

  const showReactNative = useCallback(async () => {
    await ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.PORTRAIT_UP);
    setShouldShowReactNativeScreens(true);
  }, [setShouldShowReactNativeScreens]);

  const showLegacy = useCallback(async () => {
    await ScreenOrientation.unlockAsync();
    setShouldShowReactNativeScreens(false);
    await handlePresentLegacyApp();
  }, [handlePresentLegacyApp, setShouldShowReactNativeScreens]);

  const applyMode = async (nextShowLegacy: boolean) => {
    if (!nextShowLegacy) {
      await showReactNative();
      return;
    }
    await showLegacy();
  };

  const applyLastKnownMode = async () => {
    try {
      const wasLegacy = genericStorage.get<string>("previous_app_mode") === "legacy";
      await applyMode(wasLegacy);
    } catch {
      setShouldShowReactNativeScreens(true);
    }
  };

  const resolveShowLegacy = async (): Promise<boolean | null> => {
    const flagsReady = await initFeatureFlags();
    if (flagsReady) {
      return getBooleanFlag(FEATURE_FLAGS.SHOW_LEGACY_APP_ON_STARTUP);
    }

    const lastMode = genericStorage.get<string>("previous_app_mode");
    if (lastMode) {
      return getBooleanFlag(FEATURE_FLAGS.SHOW_LEGACY_APP_ON_STARTUP);
    }

    setFeatureFlagsFailedToLoad(true);
    setShouldShowReactNativeScreens(true);
    return null;
  };

  useEffect(() => {
    const initServices = () => {
      initializeLegacyKit();
      legacyKitLifecycle.initialize();
      networkReachability.initialize();
      initAmplitude();
    };

    const initAuth = async () => {
      await migrateLegacyAuthData();
      await useAuthStore.getState().initialize();
      hydrateFeatureFlagsFromCache();
    };

    const boot = async () => {
      try {
        await ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.PORTRAIT_UP);

        if (isE2ETest()) {
          await initFeatureFlags();
          setShouldShowReactNativeScreens(true);
          finaliseBoot();
          return;
        }

        initServices();
        await initAuth();

        const nextShowLegacy = await resolveShowLegacy();
        if (nextShowLegacy === null) {
          finaliseBoot();
          return;
        }

        if (!isMountedRef.current) return;
        setPendingShowLegacy(nextShowLegacy);
      } catch (error) {
        console.warn("App init error:", error);
        await applyLastKnownMode();
        finaliseBoot();
      }
    };

    void boot();

    return () => {
      legacyKitLifecycle.cleanup();
      networkReachability.cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Safety net: if boot hasn't finalised within BOOT_TIMEOUT_MS, default to RN mode.
  // Guards against any unforeseen hung promise (e.g. a network request with no timeout).
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (finalisedRef.current) return;
      console.warn("[useAppBoot] Boot timeout — defaulting to RN mode");
      setShouldShowReactNativeScreens(true);
      finaliseBoot();
    }, BOOT_TIMEOUT_MS);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Guard: no mode decision yet.
    if (pendingShowLegacy === null) return;
    // Guard: wait for tenant resolution gate.
    if (!isTenantResolved) return;
    // Guard: boot already finalised.
    if (finalisedRef.current) return;

    const decideMode = async () => {
      try {
        const migrated = await handleAppModeTransition(pendingShowLegacy);
        if (migrated) await reinitializeAuthAfterLegacyMigration();

        void checkForceUpgrade();
        // Guard: unmounted during async work.
        if (!isMountedRef.current) return;
        if (pendingShowLegacy) {
          await showLegacy();
          return;
        }
        await showReactNative();
      } catch (error) {
        console.warn("App mode decision error:", error);
        if (!isMountedRef.current) return;
        setShouldShowReactNativeScreens(true);
      } finally {
        finaliseBoot();
      }
    };

    void decideMode();
  }, [
    pendingShowLegacy,
    isTenantResolved,
    isMountedRef,
    checkForceUpgrade,
    showLegacy,
    showReactNative,
    handlePresentLegacyApp,
    setShouldShowReactNativeScreens,
  ]);
}
